/**
 * CONTRACT.md §2.3 — Card, a bordered panel for ONE kind of thing.
 *
 * Two rules the type enforces and one it cannot. **There is no `CardFoot`** —
 * a card whose body is a full-bleed list but which still needs a closing
 * sentence takes a second `CardBody`, and app.css tightens the adjacency
 * (`.card__body--tight + .card__body`) so it reads as part of the same panel.
 * **`CardMore`** is the P3 "and the rest →" line, and it is the only other
 * thing allowed under a body.
 *
 * The one it cannot: §4.2, "don't put a head on a one-fact panel". A number and
 * a caption *are* the panel — `HeroCap` + `HeroFigure` inside a bare
 * `CardBody`, and no head at all. That is a review rule, not a type.
 */

import type { ReactNode } from 'react'

import { cx, type Common } from './common.js'
import { useCardScrollCue } from './Layout.js'

export type CardProps = Common & {
  /** `.card--ink` — the one dark panel type. */
  readonly ink?: boolean | undefined
  /** `.card--need` — an ochre border: this whole panel is waiting on you. */
  readonly need?: boolean | undefined
  readonly children: ReactNode
}

export function Card({ ink, need, children, ...common }: CardProps) {
  return (
    <div className={cx('card', ink && 'card--ink', need && 'card--need')} {...common}>
      {children}
    </div>
  )
}

export type CardHeadProps = Common & {
  /** `.card__head--rule` — the hairline under the head. Only above a table. */
  readonly rule?: boolean | undefined
  readonly children: ReactNode
}

export function CardHead({ rule, children, ...common }: CardHeadProps) {
  return (
    <div className={cx('card__head', rule && 'card__head--rule')} {...common}>
      {children}
    </div>
  )
}

export type CardTitleProps = Common & { readonly children: ReactNode }

export function CardTitle({ children, ...common }: CardTitleProps) {
  return (
    <h3 className="card__title" {...common}>
      {children}
    </h3>
  )
}

export type CardNoteProps = Common & {
  /** `.push` — the note sits at the right of the head, as in the contract's example. */
  readonly push?: boolean | undefined
  readonly children: ReactNode
}

export function CardNote({ push, children, ...common }: CardNoteProps) {
  return (
    <span className={cx('card__note', push && 'push')} {...common}>
      {children}
    </span>
  )
}

export type CardBodyProps = Common & {
  /** `.card__body--tight` — zero padding, for a full-bleed `Table` or `FileRow` list. */
  readonly tight?: boolean | undefined
  /**
   * `.scrollx` on the **same** element, which is what the mockups write.
   *
   * `<CardBody tight><ScrollX><Table/></ScrollX></CardBody>` looks equivalent and is
   * not: `.card__body--tight > .table` is a child combinator, so a wrapper between
   * them loses the edge padding, and the table loses its 640px floor on a phone.
   * `client/billing.html` writes `<div class="card__body--tight scrollx">` in three
   * places for exactly that reason.
   */
  readonly scrollX?: boolean | undefined
  readonly children: ReactNode
}

export function CardBody({ tight, scrollX, children, ...common }: CardBodyProps) {
  /*
   * R3-18. `.scrollx` is on this element and on `Layout.tsx`'s `ScrollX`, and
   * those two are the whole list — so the cue that says a table runs past the
   * right edge is in one hook and reaches both, rather than at the three call
   * sites that had thought to add one. The hook is called unconditionally and
   * answers nothing when `scrollX` is not set, which is what keeps it a hook.
   */
  const [ref, cue] = useCardScrollCue(children, scrollX === true)
  return (
    <div
      className={cx(
        tight === true ? 'card__body--tight' : 'card__body',
        scrollX === true && 'scrollx',
      )}
      ref={ref}
      // Same reason `Layout.tsx` `ScrollX` carries one: a genuinely scrollable
      // `<div>` with no focusable descendant fails WCAG 2.1.1 on a real
      // viewport (`scrollable-region-focusable`, `a11y.spec.ts`'s
      // `mobile-journeys` project). `scrollX` is the only case this element is
      // ever actually wider than its container.
      {...(scrollX === true ? { tabIndex: 0 } : {})}
      {...common}
    >
      {children}
      {cue}
    </div>
  )
}

export type CardMoreProps = Common & { readonly children: ReactNode }

/** The P3 "and the rest →" line at the bottom. No top border. */
export function CardMore({ children, ...common }: CardMoreProps) {
  return (
    <div className="card__more" {...common}>
      {children}
    </div>
  )
}
